import { useState, useMemo, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { ChevronLeft, UtensilsCrossed, Check, Minus, Plus } from 'lucide-react';
import InstructionsDisplay from './InstructionsDisplay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fixUrl = (url) => {
  if (!url || url.startsWith('data:')) return url;
  if (url.includes('.emergentagent.com/api/') || url.includes('getmealguide.com/api/')) {
    const m = url.match(/\/api\/uploads\/.+$/);
    if (m) return `${BACKEND_URL}${m[0]}`;
  }
  return url;
};

// Scale display text quantities (g, ml, kg, l + leading count numbers like "2 eggs")
const scaleText = (text, multiplier) => {
  if (!text || multiplier === 1) return text;
  let result = text.replace(/(\d+\.?\d*)\s*(g|ml|kg|l)\b/gi, (_, n, u) =>
    `${Math.round(parseFloat(n) * multiplier)}${u}`
  );
  result = result.replace(/^(\d+)\s+/, (_, n) =>
    `${Math.round(parseInt(n, 10) * multiplier)} `
  );
  return result;
};

// Ingredient name from any of the possible fields
const ingName = (ing) =>
  ing.food_bank_name || ing.food_item_name || ing.ingredient_name || ing.name || '';

// Simple category heuristic based on ingredient name
const categorize = (name = '') => {
  const n = name.toLowerCase();
  if (/chicken|beef|pork|turkey|lamb|salmon|tuna|cod|fish|egg|tofu|mince|steak|prawn|shrimp|sausage/.test(n)) return 'Proteins';
  if (/rice|oat|potato|sweet\s*potato|bread|pasta|noodle|quinoa|couscous|flour|wrap|tortilla/.test(n)) return 'Carbs';
  if (/apple|banana|berr|mango|orange|grape|peach|melon|fruit|raisin|date/.test(n)) return 'Fruits';
  if (/milk|yoghurt|yogurt|cheese|cream|butter|whey/.test(n)) return 'Dairy';
  if (/oil|sauce|vinegar|salt|pepper|seasoning|herb|spice|sugar|honey|mustard|ketchup|mayo|dressing/.test(n)) return 'Extras';
  return 'Vegetables';
};

const CATEGORY_ORDER = ['Proteins', 'Carbs', 'Vegetables', 'Fruits', 'Dairy', 'Extras'];

// Filter out cooking essentials from ingredient list
const mainIngredients = (recipe) =>
  (recipe.ingredients || []).filter(ing => {
    const essentialIds = (recipe.cooking_essentials || []).map(e => e.food_bank_id);
    return !essentialIds.includes(ing.food_bank_id || ing.id);
  });

// ─────────────────────────────────────────────────────────────────────────────

export default function MealPrepModal({ open, onClose, recipes = [] }) {
  const [screen, setScreen] = useState('select');
  const [selections, setSelections] = useState({});
  const [checked, setChecked] = useState(new Set());

  // Pre-populate selections from the incoming recipes whenever the modal opens
  useEffect(() => {
    if (open && recipes.length > 0) {
      const initial = {};
      recipes.forEach(r => { initial[r.id] = { recipe: r, servings: 1 }; });
      setSelections(initial);
      setScreen('select');
      setChecked(new Set());
    }
    if (!open) {
      setSelections({});
      setScreen('select');
      setChecked(new Set());
    }
  }, [open]);

  const toggleRecipe = (recipe) => {
    setSelections(prev => {
      if (prev[recipe.id]) {
        const next = { ...prev };
        delete next[recipe.id];
        return next;
      }
      return { ...prev, [recipe.id]: { recipe, servings: 1 } };
    });
  };

  const setServings = (id, value) => {
    setSelections(prev => ({
      ...prev,
      [id]: { ...prev[id], servings: Math.max(1, Math.min(10, value)) },
    }));
  };

  const selectedList = Object.values(selections);

  const handleStart = () => {
    setChecked(new Set());
    setScreen('prep');
  };

  const handleClose = () => {
    onClose();
  };

  const toggleCheck = (key) => {
    setChecked(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  // Aggregate all ingredients across selected recipes, grouped by category
  const summaryGroups = useMemo(() => {
    const groups = {};
    selectedList.forEach(({ recipe, servings }) => {
      mainIngredients(recipe).forEach((ing, idx) => {
        const name = ingName(ing);
        const cat = categorize(name);
        if (!groups[cat]) groups[cat] = [];
        const base = ing.display_text ||
          `${ing.quantity || ing.amount || ''} ${ing.unit || ''} ${name}`.trim();
        groups[cat].push({
          key: `${recipe.id}-${idx}`,
          text: scaleText(base, servings),
          recipeName: recipe.name,
        });
      });
    });
    return groups;
  }, [selections]);

  const checkedCount = checked.size;
  const totalCount = selectedList.reduce(
    (sum, { recipe }) => sum + mainIngredients(recipe).length, 0
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg h-[88vh] flex flex-col p-0 gap-0">

        {/* Header */}
        <DialogHeader className="px-4 pt-4 pb-3 border-b flex-shrink-0">
          <div className="flex items-center gap-2">
            {screen === 'prep' && (
              <button
                onClick={() => setScreen('select')}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                data-testid="prep-back-btn"
              >
                <ChevronLeft className="h-5 w-5 text-gray-600" />
              </button>
            )}
            <UtensilsCrossed className="h-5 w-5 text-[#1a61e8] flex-shrink-0" />
            <DialogTitle className="text-base font-semibold">Meal Prep</DialogTitle>
            {screen === 'prep' && totalCount > 0 && (
              <span className="ml-auto text-xs text-gray-400">
                {checkedCount}/{totalCount} gathered
              </span>
            )}
          </div>
        </DialogHeader>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">

          {/* ── SELECTION SCREEN ─────────────────────────────────────── */}
          {screen === 'select' && (
            <div className="p-4 space-y-2.5">
              <p className="text-xs text-gray-500 mb-3">
                Tap a recipe to deselect it, or adjust servings before starting prep.
              </p>

              {recipes.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-10">No recipes in your plan yet.</p>
              )}

              {recipes.map(recipe => {
                const selected = !!selections[recipe.id];
                const sv = selections[recipe.id]?.servings || 1;
                return (
                  <div
                    key={recipe.id}
                    onClick={() => toggleRecipe(recipe)}
                    data-testid={`prep-select-${recipe.id}`}
                    className={`flex items-center gap-3 p-3 rounded-xl border-2 cursor-pointer transition-all select-none ${
                      selected
                        ? 'border-[#1a61e8] bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    {/* Thumbnail */}
                    <div className="w-11 h-11 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                      {recipe.image_url
                        ? <img src={fixUrl(recipe.image_url)} alt="" className="w-full h-full object-cover" loading="lazy" />
                        : <div className="w-full h-full bg-gray-200" />
                      }
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-gray-900 truncate">{recipe.name}</div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        {Math.round(recipe.total_kcal || 0)} kcal · {Math.round(recipe.total_protein || 0)}g protein
                      </div>
                    </div>

                    {/* Servings + check */}
                    <div
                      className="flex items-center gap-1.5 flex-shrink-0"
                      onClick={e => e.stopPropagation()}
                    >
                      {selected && (
                        <>
                          <button
                            onClick={() => setServings(recipe.id, sv - 1)}
                            className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors"
                            data-testid={`servings-minus-${recipe.id}`}
                          >
                            <Minus className="h-3 w-3 text-gray-600" />
                          </button>
                          <span className="text-sm font-bold w-5 text-center tabular-nums">{sv}</span>
                          <button
                            onClick={() => setServings(recipe.id, sv + 1)}
                            className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100 transition-colors"
                            data-testid={`servings-plus-${recipe.id}`}
                          >
                            <Plus className="h-3 w-3 text-gray-600" />
                          </button>
                        </>
                      )}
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                          selected
                            ? 'bg-[#1a61e8]'
                            : 'border-2 border-gray-300'
                        }`}
                        onClick={() => toggleRecipe(recipe)}
                      >
                        {selected && <Check className="h-3.5 w-3.5 text-white" />}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── PREP SCREEN ──────────────────────────────────────────── */}
          {screen === 'prep' && (
            <Tabs defaultValue="summary" className="flex flex-col">
              <TabsList className="w-full justify-start px-3 pt-2 pb-0 flex-shrink-0 h-auto bg-transparent border-b rounded-none gap-1 overflow-x-auto flex-nowrap">
                <TabsTrigger
                  value="summary"
                  data-testid="prep-tab-summary"
                  className="text-xs whitespace-nowrap data-[state=active]:bg-[#1a61e8] data-[state=active]:text-white rounded-lg px-3 py-1.5 flex-shrink-0"
                >
                  Summary
                </TabsTrigger>
                {selectedList.map(({ recipe }) => (
                  <TabsTrigger
                    key={recipe.id}
                    value={recipe.id}
                    data-testid={`prep-tab-${recipe.id}`}
                    className="text-xs whitespace-nowrap data-[state=active]:bg-[#1a61e8] data-[state=active]:text-white rounded-lg px-3 py-1.5 flex-shrink-0 max-w-[130px] truncate"
                  >
                    {recipe.name.split(' ').slice(0, 3).join(' ')}
                  </TabsTrigger>
                ))}
              </TabsList>

              {/* Summary tab */}
              <TabsContent value="summary" className="p-4 space-y-5 mt-0">
                <p className="text-xs text-gray-500">
                  Pull everything out onto your counter, then tap each item as you grab it.
                </p>
                {CATEGORY_ORDER.map(cat => {
                  const items = summaryGroups[cat];
                  if (!items?.length) return null;
                  return (
                    <div key={cat}>
                      <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-2">
                        {cat}
                      </h4>
                      <div className="space-y-1.5">
                        {items.map(item => (
                          <button
                            key={item.key}
                            onClick={() => toggleCheck(item.key)}
                            data-testid={`prep-ingredient-${item.key}`}
                            className={`w-full flex items-center gap-3 p-2.5 rounded-xl border text-left transition-all ${
                              checked.has(item.key)
                                ? 'border-green-200 bg-green-50'
                                : 'border-gray-100 bg-white hover:border-gray-200 active:bg-gray-50'
                            }`}
                          >
                            <div className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 border-2 transition-colors ${
                              checked.has(item.key)
                                ? 'bg-green-500 border-green-500'
                                : 'border-gray-300'
                            }`}>
                              {checked.has(item.key) && <Check className="h-3 w-3 text-white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className={`text-sm font-medium ${
                                checked.has(item.key) ? 'line-through text-gray-300' : 'text-gray-800'
                              }`}>
                                {item.text}
                              </span>
                              <span className="text-xs text-gray-400 ml-1.5 font-normal">
                                {item.recipeName}
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </TabsContent>

              {/* Per-recipe tabs */}
              {selectedList.map(({ recipe, servings }) => {
                const ings = mainIngredients(recipe);
                const scaledKcal = Math.round((recipe.total_kcal || 0) * servings);
                const scaledProtein = Math.round((recipe.total_protein || 0) * servings);

                return (
                  <TabsContent key={recipe.id} value={recipe.id} className="p-4 space-y-4 mt-0">
                    {/* Recipe header */}
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 bg-gray-100">
                        {recipe.image_url && (
                          <img src={fixUrl(recipe.image_url)} alt="" className="w-full h-full object-cover" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm text-gray-900 truncate">{recipe.name}</h3>
                        <div className="text-xs text-gray-400">
                          {scaledKcal} kcal · {scaledProtein}g protein
                          {servings > 1 && <span className="ml-1 text-blue-500">({servings}x)</span>}
                        </div>
                      </div>
                      {/* Servings adjuster */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => setServings(recipe.id, servings - 1)}
                          className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100"
                          data-testid={`recipe-servings-minus-${recipe.id}`}
                        >
                          <Minus className="h-3 w-3 text-gray-600" />
                        </button>
                        <span className="text-sm font-bold w-6 text-center tabular-nums">{servings}x</span>
                        <button
                          onClick={() => setServings(recipe.id, servings + 1)}
                          className="w-7 h-7 rounded-full border border-gray-300 flex items-center justify-center hover:bg-gray-100"
                          data-testid={`recipe-servings-plus-${recipe.id}`}
                        >
                          <Plus className="h-3 w-3 text-gray-600" />
                        </button>
                      </div>
                    </div>

                    {/* Ingredients */}
                    {ings.length > 0 && (
                      <div>
                        <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-2">
                          Ingredients
                        </h4>
                        <ul className="space-y-2">
                          {ings.map((ing, idx) => {
                            const base = ing.display_text ||
                              `${ing.quantity || ing.amount || ''} ${ing.unit || ''} ${ingName(ing)}`.trim();
                            return (
                              <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                                <span className="text-[#1a61e8] mt-0.5 flex-shrink-0 font-bold">•</span>
                                <span>{scaleText(base, servings)}</span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}

                    {/* Instructions */}
                    {recipe.instructions && (
                      <div>
                        <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-2">
                          Instructions
                        </h4>
                        <InstructionsDisplay instructions={recipe.instructions} />
                      </div>
                    )}
                  </TabsContent>
                );
              })}
            </Tabs>
          )}
        </div>

        {/* Footer — only on selection screen */}
        {screen === 'select' && (
          <div className="px-4 py-3 border-t flex-shrink-0 bg-white">
            <Button
              onClick={handleStart}
              disabled={selectedList.length === 0}
              className="w-full bg-[#1a61e8] hover:bg-[#1a61e8]/90 h-11 text-sm font-semibold"
              data-testid="start-prep-btn"
            >
              {selectedList.length === 0
                ? 'Select recipes to start'
                : `Start Prep — ${selectedList.length} recipe${selectedList.length !== 1 ? 's' : ''}`
              }
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
